import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  IconButton,
  Box,
} from "../ui";
import CloseIcon from "@mui/icons-material/Close";

type ConfirmOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
};

type DialogHandler = (
  options: ConfirmOptions,
  resolve: (value: boolean) => void
) => void;

let showDialog: DialogHandler | null = null;
const dialogHandlers: DialogHandler[] = [];

const registerDialogHost = (handler: DialogHandler) => {
  dialogHandlers.push(handler);
  showDialog = handler;
};

const unregisterDialogHost = (handler: DialogHandler) => {
  const index = dialogHandlers.lastIndexOf(handler);
  if (index !== -1) {
    dialogHandlers.splice(index, 1);
  }
  showDialog = dialogHandlers[dialogHandlers.length - 1] ?? null;
};

export const openConfirmDialog = (
  options: ConfirmOptions
): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!showDialog) {
      console.error("ConfirmDialogHost is not mounted.");
      resolve(false);
      return;
    }
    showDialog(options, resolve);
  });
};

export const ConfirmDialogHost: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({
    title: "Confirm",
    message: "",
    confirmText: "Yes",
    cancelText: "No",
  });
  const [resolver, setResolver] = useState<(value: boolean) => void>(() => {});

  const dialogHandler = useCallback(
    (opts: ConfirmOptions, resolve: (value: boolean) => void) => {
      setOptions({
        title: opts.title ?? "Confirm",
        message: opts.message,
        confirmText: opts.confirmText ?? "Yes",
        cancelText: opts.cancelText ?? "No",
      });
      setResolver(() => resolve);
      setOpen(true);
    },
    []
  );

  useEffect(() => {
    registerDialogHost(dialogHandler);
    return () => unregisterDialogHost(dialogHandler);
  }, [dialogHandler]);

  const handleClose = () => {
    setOpen(false);
    resolver(false);
  };

  const handleConfirm = () => {
    setOpen(false);
    resolver(true);
  };

  return (
    <Dialog data-testid="components-confirm-dialog-host-modal-action" open={open} onClose={handleClose} maxWidth="xs" fullWidth sx={{ zIndex: 10000 }}>
      <DialogContent>
        <Box data-testid="components-confirm-dialog-host-container-action-1" sx={{ display: "flex", justifyContent: "space-between", pb: 2 }}>
          <Typography data-testid="components-confirm-dialog-host-text-action-1" sx={{ fontSize: 22, fontWeight: 600 }}>
            {options.title}
          </Typography>
          <IconButton data-testid="components-confirm-dialog-host-btn-handle-close-1" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography data-testid="components-confirm-dialog-host-text-action-2" sx={{ mb: 3 }}>{options.message}</Typography>

        <Box data-testid="components-confirm-dialog-host-container-action-2" sx={{ display: "flex", gap: 2 }}>
          <Button data-testid="components-confirm-dialog-host-btn-handle-close-2"
            fullWidth
            variant="outlined"
            onClick={handleClose}
            sx={{
              flex: 1,
              height: "45.074px",
              borderRadius: "10px",
              color: "#1f2937",
              fontSize: "18px",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#f3f4f6",
              border: "none",
              "&:hover": {
                backgroundColor: "#e5e7eb",
                border: "none",
              },
              "&:disabled": {
                backgroundColor: "#f3f4f6",
                color: "#9ca3af",
              },
            }}
          >
            {options.cancelText}
          </Button>
          <Button data-testid="components-confirm-dialog-host-btn-handle-confirm"
            fullWidth
            color="error"
            onClick={handleConfirm}
            sx={{
              flex: 1,
              height: "45.074px",
              backgroundColor: "#EF4444",
              borderRadius: "10px",
              color: "#fff",
              fontSize: "18px",
              fontWeight: 600,
              textTransform: "none",
              border: "none",
              "&:hover": {
                backgroundColor: "#FF0F17",
                border: "none",
              },
              "&:disabled": {
                backgroundColor: "#8300047a",
                color: "#fff",
              },
            }}
          >
            {options.confirmText}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
